// Migration pretty-printer breaks before inline tags render as spaces inside CJK text. <pre>/<code>
// newlines are real, and Hangul is excluded (Korean uses spaces).
const CJK_CHAR = /[\u2E80-\u303F\u3040-\u30FF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\uFF00-\uFFEF]/;
const TAGS = /<[^>]*>/g;
const PREFORMATTED_OR_BREAK = /<(pre|code)\b[^>]*>[\s\S]*?<\/\1\s*>|\r?\n[ \t]*/gi;
export const stripCjkSegmentBreaks = (html) => {
    if (typeof html !== 'string' || html.indexOf('\n') === -1 || !CJK_CHAR.test(html)) {
        return html;
    }
    return html.replace(PREFORMATTED_OR_BREAK, (match, preformatted, offset) => {
        if (preformatted) {
            return match;
        }
        const before = html.slice(0, offset);
        const after = html.slice(offset + match.length);
        if (!/>[ \t]*$/.test(before) && after[0] !== '<') {
            return match;
        }
        const prev = (before.replace(TAGS, '').match(/(\S)\s*$/) || [])[1] || '';
        const next = (after.replace(TAGS, '').match(/^\s*(\S)/) || [])[1] || '';
        return CJK_CHAR.test(prev) || CJK_CHAR.test(next) ? '' : match;
    });
};
export const normalizeComponentWhitespace = (component) => JSON.parse(JSON.stringify(component), (key, val) => key === 'html' || key === 'renderedHtml'
    ? stripCjkSegmentBreaks(val)
    : key === 'cells' && Array.isArray(val)
        ? val.map((row) => row.map(stripCjkSegmentBreaks))
        : val);
const MIGRATED_HEADING_LEVEL = 2;
const collectHeadings = (components) => components.flatMap((c) => c.type === 'heading'
    ? [c.value]
    : c.type === 'tabs'
        ? collectHeadings(c.value.contents.flat())
        : []);
const remapHeadings = (components) => components.map((c) => c.type === 'heading'
    ? Object.assign(Object.assign({}, c), { value: Object.assign(Object.assign({}, c.value), { level: c.value.anchorId ? 2 : 3 }) })
    : c.type === 'tabs'
        ? Object.assign(Object.assign({}, c), { value: Object.assign(Object.assign({}, c.value), { contents: c.value.contents.map((tab) => remapHeadings(tab)) }) })
        : c);
// Migration flattened every heading to level 2; until backfilled, all-level-2 articles keep anchored
// headings at 2 and move unanchored ones to 3.
export const fixMigratedHeadingLevels = (components) => {
    const headings = collectHeadings(components);
    const isMigratedSignature = headings.length > 0 && headings.every((h) => h.level === MIGRATED_HEADING_LEVEL);
    return isMigratedSignature ? remapHeadings(components) : components;
};
// Lowercases types at every nesting level (the viewer switch throws on unknown types) and drops
// chatbot-only ai_components so they never reach the page source.
const normalizeComponents = (components) => components
    .map((c) => (Object.assign(Object.assign({}, c), { type: c.type.toLowerCase() })))
    .filter((c) => c.type !== 'ai_component')
    .map((c) => c.type === 'tabs'
    ? Object.assign(Object.assign({}, c), { value: Object.assign(Object.assign({}, c.value), { contents: c.value.contents.map((tab) => normalizeComponents(tab)) }) })
    : c);
export const contentBlocksToComponents = (contentBlocks, blockOrder, { fixMigratedHeadings = false } = {}) => {
    if (!contentBlocks || !blockOrder || !blockOrder.length) {
        return [];
    }
    const components = normalizeComponents(blockOrder
        .map((id) => contentBlocks[id])
        .filter(Boolean)
        .map((block) => ({ type: block.type, value: block.value }))).map((component) => normalizeComponentWhitespace(component));
    return fixMigratedHeadings ? fixMigratedHeadingLevels(components) : components;
};
export const collectAnchoredHeadings = (components) => collectHeadings(components).filter((h) => !!h.anchorId);
const stripTags = (html) => html.replace(TAGS, ' ');
const componentText = (c) => {
    switch (c.type) {
        case 'text':
        case 'html':
            return stripTags(c.value.html);
        case 'heading':
            return c.value.text;
        case 'informative':
            return `${c.value.title || ''} ${stripTags(c.value.html)}`;
        case 'collapsible':
            return `${c.value.title} ${stripTags(c.value.html)}`;
        case 'code':
            return c.value.code;
        case 'markdown':
            return stripTags(c.value.renderedHtml || c.value.markdown);
        case 'step-by-step':
            return c.value.steps.map((s) => `${s.title} ${stripTags(s.html)}`).join(' ');
        case 'table':
            return c.value.headings.concat(...c.value.cells).join(' ');
        case 'tabs':
            return `${c.value.headings.join(' ')} ${c.value.contents.map(componentsToPlainText).join(' ')}`;
        case 'question':
            return c.value.question;
        default:
            return '';
    }
};
export const componentsToPlainText = (components) => components.map(componentText).join(' ').replace(/\s+/g, ' ').trim();
